
import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { Phone } from "lucide-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useState } from "react"
import { Link as RouterLink } from "react-router-dom"

interface Volunteer {
  id: number
  name: string
  email: string
  phone: string
  picture_url: string | null
  status: string
  remarks: string | null
  event_id: number
  meta: {
    experience: string
    availability: string
    registeredAt: string
    termsAccepted: boolean
    additionalInfo: string
    transportation: string
    waiverAccepted: boolean
    emergencyContact: {
      name: string
      phone: string
      relationship: string
    }
    dietaryRestrictions: string
  }
  created_at: string
  updated_at: string
  event: {
    slug: string
    title: string
    event_date: string
  }
}

interface VolunteersResponse {
  data: Volunteer[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export default function CommunityEventsVolunteersList({ profile, session }: { profile: any | null, session: any | null }) {
  const [page, setPage] = useState(1)
  const limit = 10

  const maskEmail = (email: string) => {
    const [username, domain] = email.split('@')
    const maskedUsername = username?.charAt(0) + '*'.repeat(username?.length - 2) + username?.charAt(username?.length - 1)
    return `${maskedUsername}@${domain}`
  }

  const maskPhone = (phone: string) => {
    const last4 = phone?.slice(-4)
    return `****${last4}`
  }

  const { data: volunteers, isLoading } = useQuery<VolunteersResponse>({
    queryKey: ["volunteers", page],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/campaign/community-events/volunteer/all?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )
      if (!response.ok) {
        throw new Error("Failed to fetch volunteers")
      }
      return response.json()
    },
    enabled: !!session?.access_token,
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case "signed_up":
        return "bg-green-500/10 text-green-700 border-green-300"
      case "waitlisted":
        return "bg-yellow-500/10 text-yellow-700 border-yellow-300"
      case "cancelled":
        return "bg-red-500/10 text-red-700 border-red-300"
      default:
        return ""
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Community Events Volunteers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Community Events Volunteers</CardTitle>
        <div className="flex items-center gap-4">
          <Badge variant="secondary">
            {volunteers?.pagination.total || 0} total volunteers
          </Badge>
          <div className="text-sm text-muted-foreground">
            Page {volunteers?.pagination.page} of {volunteers?.pagination.totalPages}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 h-[700px] overflow-y-auto pr-4">
          {volunteers?.data.map((volunteer) => (
            <div
              key={`${volunteer.id}-${volunteer.event_id}`}
              className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="grid grid-cols-12 gap-4">
                {/* Avatar and Name Section */}
                <div className="col-span-3 flex items-start gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={volunteer.picture_url || undefined} />
                    <AvatarFallback>{volunteer.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h3 className="font-medium truncate">{volunteer.name}</h3>
                    <div className="text-sm text-muted-foreground truncate">
                      {maskEmail(volunteer.email)}
                    </div>
                  </div>
                </div>

                {/* Event Details Section */}
                <div className="col-span-4">
                  <RouterLink
                    to={`/npo/community-events/${volunteer.event?.slug}`}
                    className="text-sm text-primary hover:underline line-clamp-1"
                  >
                    {volunteer.event?.title}
                  </RouterLink>
                  <div className="text-xs text-muted-foreground">
                    Event Date: {volunteer.event?.event_date ? format(new Date(volunteer.event?.event_date), "MMM d, yyyy") : ""}
                  </div>
                </div>

                {/* Contact and Status Section */}
                <div className="col-span-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {maskPhone(volunteer.phone)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Registered {volunteer.meta?.registeredAt ? format(new Date(volunteer.meta.registeredAt), "MMM d, yyyy"): ""}
                  </div>
                </div>

                {/* Badges Section */}
                <div className="col-span-2 flex items-start justify-end gap-2">
                  <Badge 
                    variant="outline" 
                    className={`capitalize ${getStatusColor(volunteer.status)}`}
                  >
                    {volunteer.status.replace("_", " ")}
                  </Badge>
                </div>
              </div>

              {/* Expandable Details */}
              <div className="grid grid-cols-12 gap-4 mt-3 pt-3 border-t text-sm">
                <div className="col-span-4">
                  <div className="flex flex-wrap gap-2">
                    {volunteer.meta?.availability && (
                      <Badge variant="secondary" className="text-xs">
                        {volunteer.meta?.availability}
                      </Badge>
                    )}
                    {volunteer.meta?.transportation && (
                      <Badge variant="secondary" className="text-xs">
                        {volunteer.meta?.transportation} transport
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="col-span-8">
                  {volunteer.meta?.emergencyContact && (
                    <div className="text-muted-foreground">
                      Emergency: {volunteer.meta?.emergencyContact.name} ({volunteer.meta?.emergencyContact.relationship}) - {maskPhone(volunteer.meta?.emergencyContact?.phone)}
                    </div>
                  )}
                  {volunteer.meta?.additionalInfo && (
                    <div className="text-muted-foreground mt-1 line-clamp-1">
                      Note: {volunteer.meta?.additionalInfo}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {(!volunteers?.data || volunteers.data.length === 0) && (
            <div className="text-center py-6 text-muted-foreground">
              No volunteers found
            </div>
          )}
        </div>

        {volunteers?.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {page} of {volunteers.pagination.totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === volunteers.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
